import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { shopApi } from "@/lib/shopApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import AuthShell, { fieldCls, primaryBtn } from "@/components/store/AuthShell";
import ResendVerification from "@/components/store/ResendVerification";

export default function Register() {
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [emailFailed, setEmailFailed] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (f.password !== f.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await shopApi.post("/member/auth/register", { name: f.name, email: f.email, password: f.password });
      setSentTo(data.email);
      setEmailFailed(data.email_sent === false);
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (sentTo)
    return (
      <AuthShell title="Check your email" testid="register-sent">
        <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
          <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
          <p className="text-sm leading-relaxed text-slate-700">{emailFailed
            ? <>Your account for <strong data-testid="register-sent-email">{sentTo}</strong> is ready, but the verification email still needs to be sent.</>
            : <>We sent a verification link to <strong data-testid="register-sent-email">{sentTo}</strong>. Click it to activate your account, then log in.</>}</p>
        </div>
        {emailFailed && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="register-email-failed">Your account was created, but we couldn't send the email just now. Please wait a minute and tap "Resend verification email".</p>}
        <ResendVerification email={sentTo} />
        <Link to="/login" data-testid="register-go-login" className="mt-6 block text-center text-sm font-semibold text-sky-700 hover:text-sky-900">Go to log in</Link>
      </AuthShell>
    );

  return (
    <AuthShell title="Create your account" subtitle="Register to access the private ATL catalogue." testid="register-page">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="r-name">Full name</Label><Input id="r-name" required minLength={2} value={f.name} onChange={set("name")} className={fieldCls} autoComplete="name" data-testid="register-name-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="r-email">Email address</Label><Input id="r-email" type="email" required value={f.email} onChange={set("email")} className={fieldCls} autoComplete="email" data-testid="register-email-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="r-pw">Password</Label><Input id="r-pw" type="password" required value={f.password} onChange={set("password")} className={fieldCls} autoComplete="new-password" placeholder="At least 8 characters" data-testid="register-password-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="r-pw2">Confirm password</Label><Input id="r-pw2" type="password" required value={f.confirm} onChange={set("confirm")} className={fieldCls} autoComplete="new-password" data-testid="register-confirm-input" /></div>
        {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="register-error">{err}</p>}
        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="register-submit-button">{busy ? "Creating account…" : "Create account"}</Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-600">Already a member? <Link to="/login" className="font-semibold text-sky-700 hover:text-sky-900" data-testid="register-login-link">Log in</Link></p>
    </AuthShell>
  );
}
